import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { VariantProps, cva } from "class-variance-authority";
import { PanelLeftIcon } from "lucide-react";

import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "5.75rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContextProps = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open]
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          data-slot="sidebar-wrapper"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn(
            "aero:group/sidebar-wrapper aero:has-data-[variant=inset]:bg-sidebar aero:flex aero:min-h-svh aero:w-full",
            className
          )}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
}

function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "aero:bg-sidebar aero:text-sidebar-foreground aero:flex aero:h-full aero:w-(--sidebar-width) aero:flex-col",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-slot="sidebar"
          data-mobile="true"
          className="aero:bg-sidebar aero:text-sidebar-foreground aero:w-(--sidebar-width) aero:p-0 aero:[&>button]:hidden"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
        >
          <SheetHeader className="aero:sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className="aero:flex aero:h-full aero:w-full aero:flex-col">
            {children}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className="aero:group aero:peer aero:text-sidebar-foreground aero:hidden aero:md:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          "aero:relative aero:w-(--sidebar-width) aero:bg-transparent aero:transition-[width] aero:duration-200 aero:ease-linear",
          "aero:group-data-[collapsible=offcanvas]:w-0",
          "aero:group-data-[side=right]:rotate-180",
          variant === "floating" || variant === "inset"
            ? "aero:group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
            : "aero:group-data-[collapsible=icon]:w-(--sidebar-width-icon)"
        )}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          "aero:relative  aero:z-10 aero:hidden aero:h-[calc(100svh-2rem-3.5rem)] aero:w-(--sidebar-width) aero:transition-[left,right,width] aero:duration-300 aero:ease-linear aero:md:flex",
          side === "left"
            ? "aero:left-0 aero:group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
            : "aero:right-0 aero:group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
          // Adjust the padding for floating and inset variants.
          variant === "floating" || variant === "inset"
            ? "aero:p-2 aero:group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
            : "aero:group-data-[collapsible=icon]:w-(--sidebar-width-icon) aero:group-data-[side=left]:border-r aero:group-data-[side=right]:border-l",
          className
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="aero:bg-sidebar aero:group-data-[variant=floating]:border-sidebar-border aero:flex aero:h-full aero:w-full aero:flex-col aero:group-data-[variant=floating]:rounded-lg aero:group-data-[variant=floating]:border aero:group-data-[variant=floating]:shadow-sm"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      design="text"
      size="iconMd"
      className={cn("aero:size-7", className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeftIcon />
      <span className="aero:sr-only">Toggle Sidebar</span>
    </Button>
  );
}

function SidebarRail({ className, ...props }: React.ComponentProps<"button">) {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "aero:hover:after:bg-sidebar-border aero:absolute aero:inset-y-0 aero:z-20 aero:hidden aero:w-4 aero:-translate-x-1/2 aero:transition-all aero:ease-linear aero:group-data-[side=left]:-right-4 aero:group-data-[side=right]:left-0 aero:after:absolute aero:after:inset-y-0 aero:after:left-1/2 aero:after:w-[2px] aero:sm:flex",
        "aero:in-data-[side=left]:cursor-w-resize aero:in-data-[side=right]:cursor-e-resize",
        "aero:[[data-side=left][data-state=collapsed]_&]:cursor-e-resize aero:[[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "aero:hover:group-data-[collapsible=offcanvas]:bg-sidebar aero:group-data-[collapsible=offcanvas]:translate-x-0 aero:group-data-[collapsible=offcanvas]:after:left-full",
        "aero:[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "aero:[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className
      )}
      {...props}
    />
  );
}

function SidebarInset({ className, ...props }: React.ComponentProps<"main">) {
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        "aero:bg-background aero:relative aero:flex aero:w-full aero:flex-1 aero:flex-col",
        "aero:md:peer-data-[variant=inset]:m-2 aero:md:peer-data-[variant=inset]:ml-0 aero:md:peer-data-[variant=inset]:rounded-xl aero:md:peer-data-[variant=inset]:shadow-sm aero:md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2",
        className
      )}
      {...props}
    />
  );
}

function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn(
        "aero:bg-background aero:h-8 aero:w-full aero:shadow-none",
        className
      )}
      {...props}
    />
  );
}

function SidebarHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn(
        "aero:flex aero:flex-col aero:gap-2 aero:px-2 aero:py-1",
        className
      )}
      {...props}
    />
  );
}

function SidebarFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn("aero:flex aero:flex-col aero:gap-2 aero:p-2", className)}
      {...props}
    />
  );
}

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn("aero:bg-sidebar-border aero:mx-2 aero:w-auto", className)}
      {...props}
    />
  );
}

function SidebarContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "aero:flex aero:min-h-0 aero:flex-1 aero:flex-col aero:gap-2 aero:overflow-auto aero:group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      {...props}
    />
  );
}

function SidebarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn(
        "aero:relative aero:flex aero:w-full aero:min-w-0 aero:flex-col aero:p-2",
        className
      )}
      {...props}
    />
  );
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        "aero:text-sidebar-foreground/70 aero:ring-sidebar-ring aero:flex aero:h-8 aero:shrink-0 aero:items-center aero:!rounded-md aero:px-2 aero:text-xs aero:font-medium aero:outline-hidden aero:transition-[margin,opacity] aero:duration-200 aero:ease-linear aero:focus-visible:ring-2 aero:[&>svg]:size-4 aero:[&>svg]:shrink-0",
        "aero:group-data-[collapsible=icon]:-mt-8 aero:group-data-[collapsible=icon]:opacity-0",
        className
      )}
      {...props}
    />
  );
}

function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        "aero:text-sidebar-foreground aero:ring-sidebar-ring aero:hover:bg-sidebar-accent aero:hover:text-sidebar-accent-foreground aero:absolute aero:top-3.5 aero:right-3 aero:flex aero:aspect-square aero:w-5 aero:items-center aero:justify-center aero:!rounded-md aero:p-0 aero:outline-hidden aero:transition-transform aero:focus-visible:ring-2 aero:[&>svg]:size-4 aero:[&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "aero:after:absolute aero:after:-inset-2 aero:md:after:hidden",
        "aero:group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  );
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn("aero:w-full aero:text-sm", className)}
      {...props}
    />
  );
}

function SidebarMenu({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn(
        "aero:flex aero:w-full aero:min-w-0 aero:flex-col aero:gap-1",
        className
      )}
      {...props}
    />
  );
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn("aero:group/menu-item aero:relative", className)}
      {...props}
    />
  );
}

const sidebarMenuButtonVariants = cva(
  "aero:peer/menu-button aero:flex aero:w-full aero:items-center aero:gap-2 aero:overflow-hidden aero:!rounded-md aero:p-2 aero:text-left aero:text-sm aero:outline-hidden aero:ring-sidebar-ring aero:transition-[width,height,padding] aero:hover:bg-sidebar-accent aero:hover:text-sidebar-accent-foreground aero:focus-visible:ring-2 aero:active:bg-sidebar-accent aero:active:text-sidebar-accent-foreground aero:disabled:pointer-events-none aero:disabled:opacity-50 aero:group-has-data-[sidebar=menu-action]/menu-item:pr-8 aero:aria-disabled:pointer-events-none aero:aria-disabled:opacity-50 aero:data-[active=true]:bg-sidebar-accent aero:data-[active=true]:font-medium aero:data-[active=true]:text-sidebar-accent-foreground aero:data-[state=open]:hover:bg-sidebar-accent aero:data-[state=open]:hover:text-sidebar-accent-foreground aero:group-data-[collapsible=icon]:size-8! aero:group-data-[collapsible=icon]:p-2! aero:[&>span:last-child]:truncate aero:[&>svg]:size-4 aero:[&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "aero:hover:bg-sidebar-accent aero:hover:text-sidebar-accent-foreground",
        outline:
          "aero:bg-background aero:shadow-[0_0_0_1px_hsl(var(--sidebar-border))] aero:hover:bg-sidebar-accent aero:hover:text-sidebar-accent-foreground aero:hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
      },
      size: {
        default: "aero:h-8 aero:text-sm",
        sm: "aero:h-7 aero:text-xs",
        lg: "aero:h-12 aero:text-sm aero:group-data-[collapsible=icon]:p-0!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  const Comp = asChild ? Slot : "button";
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
}

function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        "aero:text-sidebar-foreground aero:ring-sidebar-ring aero:hover:bg-sidebar-accent aero:hover:text-sidebar-accent-foreground aero:peer-hover/menu-button:text-sidebar-accent-foreground aero:absolute aero:top-1.5 aero:right-1 aero:flex aero:aspect-square aero:w-5 aero:items-center aero:justify-center aero:!rounded-md aero:p-0 aero:outline-hidden aero:transition-transform aero:focus-visible:ring-2 aero:[&>svg]:size-4 aero:[&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "aero:after:absolute aero:after:-inset-2 aero:md:after:hidden",
        "aero:peer-data-[size=sm]/menu-button:top-1",
        "aero:peer-data-[size=default]/menu-button:top-1.5",
        "aero:peer-data-[size=lg]/menu-button:top-2.5",
        "aero:group-data-[collapsible=icon]:hidden",
        showOnHover &&
          "aero:peer-data-[active=true]/menu-button:text-sidebar-accent-foreground aero:group-focus-within/menu-item:opacity-100 aero:group-hover/menu-item:opacity-100 aero:data-[state=open]:opacity-100 aero:md:opacity-0",
        className
      )}
      {...props}
    />
  );
}

function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        "aero:text-sidebar-foreground aero:pointer-events-none aero:absolute aero:right-1 aero:flex aero:h-5 aero:min-w-5 aero:items-center aero:justify-center aero:!rounded-md aero:px-1 aero:text-xs aero:font-medium aero:tabular-nums aero:select-none",
        "aero:peer-hover/menu-button:text-sidebar-accent-foreground aero:peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
        "aero:peer-data-[size=sm]/menu-button:top-1",
        "aero:peer-data-[size=default]/menu-button:top-1.5",
        "aero:peer-data-[size=lg]/menu-button:top-2.5",
        "aero:group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  );
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<"div"> & {
  showIcon?: boolean;
}) {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn(
        "aero:flex aero:h-8 aero:items-center aero:gap-2 aero:!rounded-md aero:px-2",
        className
      )}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="aero:size-4 aero:!rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="aero:h-4 aero:max-w-(--skeleton-width) aero:flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        "aero:border-sidebar-border aero:mx-3.5 aero:flex aero:min-w-0 aero:translate-x-px aero:flex-col aero:gap-1 aero:border-l aero:px-2.5 aero:py-0.5",
        "aero:group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  );
}

function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn("aero:group/menu-sub-item aero:relative", className)}
      {...props}
    />
  );
}

function SidebarMenuSubButton({
  asChild = false,
  size = "md",
  isActive = false,
  className,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean;
  size?: "sm" | "md";
  isActive?: boolean;
}) {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "aero:text-sidebar-foreground aero:ring-sidebar-ring aero:hover:bg-sidebar-accent aero:hover:text-sidebar-accent-foreground aero:active:bg-sidebar-accent aero:active:text-sidebar-accent-foreground aero:[&>svg]:text-sidebar-accent-foreground aero:flex aero:h-7 aero:min-w-0 aero:-translate-x-px aero:items-center aero:gap-2 aero:overflow-hidden aero:!rounded-md aero:px-2 aero:outline-hidden aero:focus-visible:ring-2 aero:disabled:pointer-events-none aero:disabled:opacity-50 aero:aria-disabled:pointer-events-none aero:aria-disabled:opacity-50 aero:[&>span:last-child]:truncate aero:[&>svg]:size-4 aero:[&>svg]:shrink-0",
        "aero:data-[active=true]:bg-sidebar-accent aero:data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "aero:text-xs",
        size === "md" && "aero:text-sm",
        "aero:group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  );
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
